import axios from "axios";
import {
  getApiBaseUrl,
  getBearerToken,
  logApiConfig,
} from "../utils/config.js";

const API_BASE_URL = getApiBaseUrl();
const BEARER_TOKEN = getBearerToken();

// Log configuration status on import
logApiConfig();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Authorization: BEARER_TOKEN ? `Bearer ${BEARER_TOKEN}` : undefined,
    "Content-Type": "application/json",
  },
});

// Add request interceptor to handle missing auth token
api.interceptors.request.use(
  (config) => {
    if (!BEARER_TOKEN && config.headers.Authorization) {
      console.warn("Bearer token is missing, removing Authorization header");
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const fetchBlogs = async (page = 0) => {
  try {
    const response = await api.get(`/api/v1/news/list?page=${page}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching blogs:", error);
    throw error;
  }
};

export const searchBlogs = async (q) => {
  try {
    const response = await api.get(`/api/v1/news/search?q=${q}`);
    return response.data;
  } catch (error) {
    console.error("Error searching blogs:", error);
    throw error;
  }
};

export const fetchBlogDetails = async (postId) => {
  try {
    const response = await api.get(`/api/v1/news/${postId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching blog details:", error);
    throw error;
  }
};

export const sendMessage = async (formData) => {
  try {
    const response = await api.post("/api/v1/message", formData);
    return response.data;
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};

export const fetchChatMessages = async (
  senderEmail,
  recipientEmail,
  currentPage,
  pageSize
) => {
  try {
    const response = await api.get(
      `/api/v1/chat/list?sender=${senderEmail}&recipient=${recipientEmail}&page=${
        currentPage ?? 0
      }&size=${pageSize ?? 20}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching chat messages:", error);
    throw error;
  }
};

export const fetchProperties = async (page = 0) => {
  try {
    const response = await api.get(`/api/v1/house/list?page=${page}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching properties:", error);
    throw error;
  }
};

export const fetchOffers = async (page = 0) => {
  try {
    const response = await api.get(`/api/v1/offer/list?page=${page}`);

    if (response.data && response.data.success && response.data.payload) {
      return response.data; // Return the original response structure
    }
    throw new Error("No offers found or response is invalid");
  } catch (error) {
    console.error("Error fetching offers:", error.message);
    if (error.response) {
      console.error("API Response Error:", error.response.data);
    }
    return {
      payload: [],
      pagination: { currentPage: 0, totalPages: 1, size: 10, totalItems: 0 },
    };
  }
};

export const fetchPropertyById = async (id) => {
  try {
    const response = await api.get(`/api/v1/house/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching property details:", error);
    throw error;
  }
};
export const fetchPlotById = async (id) => {
  try {
    const response = await api.get(`/api/v1/plot/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching property details:", error);
    throw error;
  }
};

export const searchOffers = async (query) => {
  try {
    const response = await api.get(
      `/api/v1/offer/search?q=${encodeURIComponent(query)}`
    );

    if (response.data && response.data.success) {
      return response.data; // Return the original response structure
    }
    throw new Error("No matching offers found");
  } catch (error) {
    console.error("Error searching offers:", error.message);
    if (error.response) {
      console.error("API Response Error:", error.response.data);
    }
    return {
      payload: [],
      pagination: { currentPage: 0, totalPages: 0, size: 10, totalItems: 0 },
    };
  }
};

export const searchProperties = async (q) => {
  try {
    const response = await api.get(`/api/v1/property-listing/search?q=${q}`);
    if (
      response.data &&
      response.data.success &&
      response.data.payload &&
      response.data.payload.page
    ) {
      return response.data;
    } else {
      throw new Error("No properties found or response is invalid");
    }
  } catch (error) {
    console.error("Error fetching properties:", error.message);

    if (error.response) {
      console.error("API Response Error:", error.response.data);
    }
    return [];
  }
};
export const fetchFaqs = async (page = 0) => {
  try {
    const response = await api.get(`/api/v1/faq/list?page=${page}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching FAQs:", error);
    throw error;
  }
};

export const fetchPosts = async (page = 1, search = "") => {
  try {
    const endpoint = search
      ? `/api/v1/news/search?q=${search}&page=${page}`
      : `/api/v1/news/list?page=${page}`;
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error("Error fetching posts:", error);
    throw error;
  }
};

export const submitInspectionRequest = async (formData, propertyId) => {
  try {
    const payload = {
      name: formData.name,
      phoneNumber: formData.phoneNumber,
      emailAddress: formData.emailAddress,
      propertyId: propertyId || undefined,
      preferredDate: formData.preferredDate,
      preferredTime: formData.preferredTime,
      message: formData.message || "",
    };

    console.log("Submitting inspection request...");
    console.log("API Base URL:", API_BASE_URL);
    console.log("Bearer Token:", BEARER_TOKEN ? "Present" : "Missing");
    console.log("Payload:", payload);

    if (BEARER_TOKEN) {
      try {
        console.log("Attempting authenticated request...");
        const response = await api.post("/api/v1/inspection-request", payload);
        console.log("Authenticated request successful:", response.data);
        return response.data;
      } catch (authError) {
        console.log(
          "Authenticated request failed:",
          authError.response?.status,
          authError.response?.data
        );

        if (authError.response?.status === 401) {
          console.log("401 error detected, trying public request...");
        } else {
          throw authError;
        }
      }
    } else {
      console.log("No bearer token, proceeding with public request...");
    }

    console.log("Attempting public API request...");

    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    try {
      const response = await publicApi.post(
        "/api/v1/inspection-request",
        payload
      );
      console.log("Public API request successful:", response.data);
      return response.data;
    } catch (publicError) {
      console.error(
        "Public API request failed:",
        publicError.response?.status,
        publicError.response?.data
      );

      throw publicError;
    }
  } catch (error) {
    console.error("Final error in inspection request:", error);

    let errorMessage = "Failed to submit inspection request. Please try again.";

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    const clearError = new Error(errorMessage);
    clearError.originalError = error;
    clearError.response = error.response;

    throw clearError;
  }
};

export const fetchOfferDetails = async (id) => {
  try {
    const response = await api.get(`/api/v1/offer/detail?id=${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching offer details:", error);
    throw error;
  }
};
export const subscribeNewsletter = async (emailAddress) => {
  try {
    const response = await api.post("/api/v1/news/subscribe", { emailAddress });
    return response.data;
  } catch (error) {
    console.error("Error subscribing to newsletter:", error);
    throw error;
  }
};
export const submitPurchase = async (formData) => {
  try {
    const response = await api.post("/api/v1/sale/form", formData);
    return response.data;
  } catch (error) {
    console.error("Error submitting purchase:", error);
    throw error;
  }
};

export const fetchAnnouncements = async (page = 0) => {
  try {
    const response = await api.get(`/api/v1/announcement/list?page=${page}`);
    // Return the array of announcements from the response data
    return response.data.payload?.page || [];
  } catch (error) {
    console.error("Error fetching announcements:", error);
    throw error;
  }
};

export const fetchAvailableProperties = async (page = 0) => {
  try {
    const response = await api.get(`/api/v1/property/list?page=${page}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching available properties:", error);
    throw error;
  }
};

export const fetchEstates = async (page = 0) => {
  try {
    const response = await api.get(`/api/v1/estate/public/list?page=${page}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching estates:", error);
    throw error;
  }
};

export const getEstateById = async (estateId) => {
  try {
    const response = await api.get(`/api/v1/estate/details/${estateId}`);

    if (response.data && response.data.success) {
      return response.data;
    }
    throw new Error(response.data?.message || "Estate not found");
  } catch (error) {
    console.error("Error fetching Estate details:", error);
    throw error;
  }
};

export const searchEstates = async (query) => {
  try {
    const response = await api.get(`/api/v1/estate/public/search?q=${query}`);
    return response.data;
  } catch (error) {
    console.error("Error searching estates:", error);
    throw error;
  }
};

export const getPlotById = async (plotId) => {
  try {
    const response = await api.get(`/api/v1/plot/details/${plotId}`);

    if (response.data && response.data.success) {
      return response.data.payload;
    }
    throw new Error(response.data?.message || "Plot not found");
  } catch (error) {
    console.error("Error fetching plot details:", error);
    throw error;
  }
};

export const fetchHouses = async (page = 0, size = 10, filters = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...filters,
    });

    const response = await api.get(`/api/v1/house/list?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching houses:", error);
    throw error;
  }
};

export const fetchPublicPlots = async (page = 0, size = 10, filters = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...filters,
    });

    const response = await api.get(`/api/v1/plot/public/list?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching public plots:", error);
    throw error;
  }
};

export const searchPublicPlots = async (
  query,
  page = 0,
  size = 10,
  filters = {}
) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      q: query,
      ...filters,
    });

    const response = await api.get(`/api/v1/plot/public/search?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error("Error searching public plots:", error);
    throw error;
  }
};

export const fetchPublicLayouts = async (page = 0, size = 10, filters = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page,
      size,
      ...filters,
    }).toString();

    const response = await api.get(`/api/v1/layout/public/list?${queryParams}`);

    if (response.data && response.data.success && response.data.payload) {
      return {
        ...response.data,
        payload: response.data.payload.page, // Extract the page array
        pagination: {
          currentPage: response.data.payload.currentPage,
          totalPages: response.data.payload.totalPages,
          size: response.data.payload.size,
          totalItems:
            response.data.payload.totalElements ||
            response.data.payload.page?.length ||
            0,
        },
      };
    }
    throw new Error(response.data?.message || "Failed to fetch layouts");
  } catch (error) {
    console.error("Error fetching layouts:", error);
    throw error;
  }
};

export const searchPublicLayouts = async (
  query,
  page = 0,
  size = 10,
  filters = {}
) => {
  try {
    const queryParams = new URLSearchParams({
      q: query,
      page,
      size,
      ...filters,
    }).toString();

    const response = await api.get(
      `/api/v1/layout/public/search?${queryParams}`
    );

    if (response.data && response.data.success && response.data.payload) {
      return {
        ...response.data,
        payload: response.data.payload.page, // Extract the page array
        pagination: {
          currentPage: response.data.payload.currentPage,
          totalPages: response.data.payload.totalPages,
          size: response.data.payload.size,
          totalItems:
            response.data.payload.totalElements ||
            response.data.payload.page?.length ||
            0,
        },
      };
    }
    throw new Error(response.data?.message || "No matching layouts found");
  } catch (error) {
    console.error("Error searching layouts:", error);
    throw error;
  }
};

export const getLayoutById = async (layoutId) => {
  try {
    const response = await api.get(`/api/v1/layout/details/${layoutId}`);

    if (response.data && response.data.success) {
      return response.data;
    }
    throw new Error(response.data?.message || "Layout not found");
  } catch (error) {
    console.error("Error fetching layout details:", error);
    throw error;
  }
};

export default api;
